import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import clarity_notification_line from "./../../../assets/global_assets/nav_imgs/clarity_notification-line.png";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../../services/global_redux/global_api_services/Notifications.api";
import socket from "../../../services/socket";
import "./NotificationBell.css";

const normalizeInboxService = (value) => (
  String(value || "Register").toLowerCase() === "audit" ? "Audit" : "Register"
);

const matchesInboxService = (item, inboxService) => {
  const value = String(item?.service || "Register").toLowerCase();
  if (inboxService === "Audit") return value === "audit";
  return value !== "audit";
};

const auditNotificationLines = (item) => {
  const details = item?.event_details || {};
  const branch = details.branch_label;
  const name = details.audit_name;
  const status = details.status_label;
  if (branch || name || status) {
    return [
      branch ? `Branch : ${branch}` : "",
      name ? `Audit Name : ${name}` : "",
      status ? `Status : ${status}` : "",
    ].filter(Boolean);
  }
  return String(item?.title || "").split("\n").map((line) => line.trim()).filter(Boolean);
};

function NotificationBell({ service = "Register" }) {
  const navigate = useNavigate();
  const inboxService = normalizeInboxService(service);
  const sessionUser = JSON.parse(sessionStorage.getItem("user") || "null");
  const isClientUser = sessionUser?.base_role === "Client";
  const currentUserId = sessionUser?.user_id;

  const [notifOpen, setNotifOpen] = useState(false);
  const [notifDialogOpen, setNotifDialogOpen] = useState(false);
  const [notifTab, setNotifTab] = useState("unread");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [liveToasts, setLiveToasts] = useState([]);
  const [bellPulse, setBellPulse] = useState(false);
  const notifRef = useRef(null);
  const knownNotifIdsRef = useRef(new Set());
  const notificationsReadyRef = useRef(false);

  const pushLiveToast = (fresh) => {
    if (!fresh.length) return;
    const toast = {
      key: `${Date.now()}-${fresh[0].id}`,
      count: fresh.length,
      title: fresh[0].title,
      item: fresh[0],
    };
    setLiveToasts((prev) => [...prev.slice(-2), toast]);
    setBellPulse(true);
    window.setTimeout(() => setBellPulse(false), 2200);
    window.setTimeout(() => {
      setLiveToasts((prev) => prev.filter((row) => row.key !== toast.key));
    }, 8000);
  };

  const ingestNotifications = (payload, { announce } = {}) => {
    const rows = (Array.isArray(payload?.data) ? payload.data : []).filter((row) =>
      matchesInboxService(row, inboxService)
    );
    const unread = Number.isFinite(Number(payload?.unread_count))
      ? Number(payload.unread_count)
      : rows.filter((row) => !row.is_read).length;
    if (announce && notificationsReadyRef.current) {
      const fresh = rows.filter((row) => !knownNotifIdsRef.current.has(row.id) && !row.is_read);
      if (fresh.length) pushLiveToast(fresh);
    }
    rows.forEach((row) => knownNotifIdsRef.current.add(row.id));
    setNotifications(rows);
    setUnreadCount(unread);
    notificationsReadyRef.current = true;
  };

  const loadNotifications = async ({ announce } = {}) => {
    if (!isClientUser) return;
    try {
      const payload = await fetchNotifications(inboxService);
      ingestNotifications(payload, { announce });
    } catch {
      if (!announce) {
        setNotifications([]);
        setUnreadCount(0);
      }
    }
  };

  useEffect(() => {
    knownNotifIdsRef.current = new Set();
    notificationsReadyRef.current = false;
    setNotifications([]);
    setUnreadCount(0);
    setLiveToasts([]);
    setNotifOpen(false);
    setNotifDialogOpen(false);
  }, [inboxService]);

  useEffect(() => {
    if (!isClientUser) return undefined;
    loadNotifications({ announce: false });
    const poll = window.setInterval(() => loadNotifications({ announce: true }), 12000);
    return () => window.clearInterval(poll);
  }, [isClientUser, inboxService]);

  useEffect(() => {
    if (!isClientUser) return undefined;
    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
    if (token) {
      socket.auth = { token };
      if (!socket.connected) socket.connect();
    }
    const userId = String(currentUserId || "");
    const onBroadcast = (data) => {
      if (data?.type !== "client_form_notification" && data?.type !== "client_audit_notification") return;
      const mine = (Array.isArray(data.items) ? data.items : []).filter(
        (item) => String(item.user_id) === userId && matchesInboxService(item, inboxService)
      );
      if (!mine.length) return;
      const fresh = mine.filter((item) => !knownNotifIdsRef.current.has(item.id));
      if (!fresh.length) return;
      fresh.forEach((row) => knownNotifIdsRef.current.add(row.id));
      setNotifications((prev) => {
        const ids = new Set(fresh.map((row) => row.id));
        return [...fresh, ...prev.filter((row) => !ids.has(row.id))];
      });
      setUnreadCount((count) => count + fresh.length);
      pushLiveToast(fresh);
    };
    socket.on("register_broadcast", onBroadcast);
    socket.on("audit_broadcast", onBroadcast);
    return () => {
      socket.off("register_broadcast", onBroadcast);
      socket.off("audit_broadcast", onBroadcast);
    };
  }, [isClientUser, currentUserId, inboxService]);

  useEffect(() => {
    if (!notifOpen) return;
    const onDocClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [notifOpen]);

  const unreadNotifications = notifications.filter((item) => !item.is_read);
  const readNotifications = notifications.filter((item) => item.is_read);
  const panelRows = (notifTab === "unread" ? unreadNotifications : readNotifications).slice(0, 5);
  const panelHasMore = (notifTab === "unread" ? unreadNotifications : readNotifications).length > 5;

  const openNotification = () => {
    if (!isClientUser) return;
    setNotifOpen((prev) => {
      const next = !prev;
      if (next) setNotifTab(unreadCount > 0 ? "unread" : "read");
      return next;
    });
    loadNotifications();
  };

  const dismissLiveToast = (key) => {
    setLiveToasts((prev) => prev.filter((row) => row.key !== key));
  };

  const openAllNotifications = (e) => {
    if (e) e.stopPropagation();
    setNotifOpen(false);
    setNotifTab(unreadCount > 0 ? "unread" : "read");
    setNotifDialogOpen(true);
    loadNotifications();
  };

  const handleNotificationClick = async (item, fromDialog = false) => {
    if (item?.id && !item.is_read) {
      try {
        await markNotificationRead(item.id);
        setNotifications((prev) =>
          prev.map((row) => (row.id === item.id ? { ...row, is_read: 1 } : row))
        );
        setUnreadCount((n) => Math.max(0, n - 1));
      } catch {
        /* keep list open even if mark-read fails */
      }
    }
    setNotifOpen(false);
    if (fromDialog) setNotifDialogOpen(false);
    if (item?.link) navigate(item.link);
  };

  const handleToastClick = (toast) => {
    dismissLiveToast(toast.key);
    if (toast?.item) handleNotificationClick(toast.item);
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    try {
      await markAllNotificationsRead(inboxService);
      setNotifications((prev) => prev.map((row) => ({ ...row, is_read: 1 })));
      setUnreadCount(0);
      setNotifTab("read");
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <div className={`icon-btn bell-notification-icon-bg position-relative${bellPulse ? " is-live" : ""}`} ref={notifRef}>
        <button type="button" className="nav-bell-trigger" onClick={() => openNotification()} aria-label="Notifications">
          <img src={clarity_notification_line} alt="" />
        </button>
        {isClientUser && unreadCount > 0 ? (
          <span className="badge bg-danger notifation-counts p-1">{unreadCount > 9 ? "9+" : unreadCount}</span>
        ) : null}
        {notifOpen && isClientUser ? (
          <div className="nav-notification-panel">
            <div className="nav-notification-head">
              <div>
                <p>Inbox</p>
                <strong>Notifications</strong>
              </div>
              <button type="button" onClick={openAllNotifications}>Show all</button>
            </div>
            <div className="nav-notification-tabs nav-notification-tabs-panel" role="tablist">
              <span
                className="nav-notification-tab-slider"
                style={{ transform: notifTab === "read" ? "translateX(100%)" : "translateX(0)" }}
              />
              <button
                type="button"
                role="tab"
                aria-selected={notifTab === "unread"}
                className={notifTab === "unread" ? "is-active" : ""}
                onClick={() => setNotifTab("unread")}
              >
                Unread
                <em>{unreadNotifications.length}</em>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={notifTab === "read"}
                className={notifTab === "read" ? "is-active" : ""}
                onClick={() => setNotifTab("read")}
              >
                Read
                <em>{readNotifications.length}</em>
              </button>
            </div>
            <div className="nav-notification-panel-body">
              {panelRows.length ? (
                <ul>
                  {panelRows.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={item.is_read ? "" : "is-unread"}
                        onClick={() => handleNotificationClick(item)}
                      >
                        <span className="nav-notification-dot" />
                        <span className="nav-notification-copy">
                          {inboxService === "Audit" ? (
                            auditNotificationLines(item).map((line) => <span key={line}>{line}</span>)
                          ) : (
                            <span>{item.title}</span>
                          )}
                          <small>{item.created_at_display || ""}</small>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="nav-notification-empty">
                  {notifTab === "unread" ? "You're all caught up." : "Nothing in Read yet."}
                </p>
              )}
            </div>
            {panelHasMore ? (
              <button type="button" className="nav-notification-more" onClick={openAllNotifications}>
                See more
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <Dialog
        open={notifDialogOpen}
        onClose={() => setNotifDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        aria-labelledby="all-notifications-title"
        sx={{ zIndex: 10050 }}
        PaperProps={{ className: "nav-notification-dialog-paper" }}
      >
        <div className="nav-notification-dialog">
          <div className="nav-notification-dialog-chrome">
            <div className="nav-notification-dialog-hero">
              <div>
                <p>Inbox</p>
                <h2 id="all-notifications-title">Notifications</h2>
                <small>{unreadCount} unread · {readNotifications.length} read</small>
              </div>
              <button type="button" className="nav-notification-dialog-close" onClick={() => setNotifDialogOpen(false)}>
                ×
              </button>
            </div>
            <div
              className="nav-notification-tabs"
              role="tablist"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                  e.preventDefault();
                  setNotifTab((tab) => (tab === "unread" ? "read" : "unread"));
                }
              }}
            >
              <span
                className="nav-notification-tab-slider"
                style={{ transform: notifTab === "read" ? "translateX(100%)" : "translateX(0)" }}
              />
              <button
                type="button"
                role="tab"
                aria-selected={notifTab === "unread"}
                className={notifTab === "unread" ? "is-active" : ""}
                onClick={() => setNotifTab("unread")}
              >
                Unread
                <em>{unreadNotifications.length}</em>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={notifTab === "read"}
                className={notifTab === "read" ? "is-active" : ""}
                onClick={() => setNotifTab("read")}
              >
                Read
                <em>{readNotifications.length}</em>
              </button>
            </div>
            {notifTab === "unread" && unreadCount > 0 ? (
              <div className="nav-notification-dialog-toolbar">
                <button type="button" onClick={handleMarkAllRead}>Mark all as read</button>
              </div>
            ) : null}
          </div>
          <div className="nav-notification-dialog-body">
            {(() => {
              const rows = notifTab === "unread" ? unreadNotifications : readNotifications;
              if (!rows.length) {
                return (
                  <div className="nav-notification-empty-card" key={notifTab}>
                    <strong>{notifTab === "unread" ? "You're all caught up" : "Nothing in Read yet"}</strong>
                    <span>
                      {notifTab === "unread"
                        ? (inboxService === "Audit"
                          ? "New audit updates will appear here."
                          : "New released forms will appear here.")
                        : "Open an unread item and it will move here."}
                    </span>
                  </div>
                );
              }
              return (
                <ul className="nav-notification-dialog-list" key={notifTab}>
                  {rows.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={item.is_read ? "is-read-card" : "is-unread"}
                        onClick={() => handleNotificationClick(item, true)}
                      >
                        <span className="nav-notification-dot" />
                        <span className="nav-notification-copy">
                          {inboxService === "Audit" ? (
                            auditNotificationLines(item).map((line) => <span key={line}>{line}</span>)
                          ) : (
                            <span>{item.title}</span>
                          )}
                          <small>{item.created_at_display || ""}</small>
                        </span>
                        <span className="nav-notification-open">Open</span>
                      </button>
                    </li>
                  ))}
                </ul>
              );
            })()}
          </div>
        </div>
      </Dialog>

      {isClientUser && liveToasts.length && typeof document !== "undefined"
        ? createPortal(
          <div className="nav-live-toast-stack">
            {liveToasts.map((toast) => (
              <div key={toast.key} className="nav-live-toast">
                <span className="nav-live-toast-bell" aria-hidden="true">🔔</span>
                <div className="nav-live-toast-copy">
                  <strong>{toast.count > 1 ? `You have ${toast.count} new notifications` : "You have a new notification"}</strong>
                  <span>{inboxService === "Audit" ? auditNotificationLines(toast.item).join(" · ") : toast.title}</span>
                  <div className="nav-live-toast-actions">
                    <button type="button" onClick={() => handleToastClick(toast)}>Open</button>
                    <button type="button" onClick={() => { dismissLiveToast(toast.key); openAllNotifications(); }}>See all</button>
                  </div>
                </div>
                <button type="button" className="nav-live-toast-close" onClick={() => dismissLiveToast(toast.key)}>×</button>
              </div>
            ))}
          </div>,
          document.body
        )
        : null}
    </>
  );
}

export default NotificationBell;
